/** @jsxImportSource ./simboluri-jsx */
import { useEffect, useState } from "react";
import { bani } from "./bani";
import Marime, { EtichetaNatura } from "./Marime";

import AvertismenteMetoda from "./AvertismenteMetoda";
import Info from "./Info";
import { numar, t, tn, tx, useT, type Cheie } from "../i18n";
import { Legenda, Pas, Verdict } from "./Explica";
import TitluVerificare, { useCatalogVerificari } from "./Verificare";
import * as D from "./DiagrameFundatie";
import TabelCombinatiiAx, { Da } from "./TabelCombinatiiAx";
import { CautareGeometrie, RezistentaResult, VerificareSumar, VerificariFundatie } from "./tipuri";
import { fundatiiCsvUrl, getPrefs, savePrefs } from "./apiStructura";
import { useRolProiect } from "../components/rol";
import { traduceRezultat } from "./texteRezultat";

// Pasul 17 — verificarea fundației pe reacțiunile reale ale modelului,
// per ax (structura foilor AxA, AxB, … din metoda de lucru), per stâlp și
// global. Fiecare ax primește TREI lecturi ale aceleiași presiuni:
//   metoda   p₁ = ΣN/A ± ΣM/W, cu pragul 1,10 / 1,35 · p_conv
//   NP 112   p_ef,max = μ·N/(L·B), cu pragul din tab. I.5
//   model    max(R_resort / (B · l_aferentă)) — cea mai fidelă, din OpenSees

// De ce fundația are DOI pași. Explicația stă atât pe starea de așteptare
// (când Pasul 17 n-are reacțiuni), cât și pe rezultatul complet — e prima
// întrebare pe care o pune oricine vede două pagini de fundație și numerele
// schimbate față de ce arăta Pasul 7.
function ExplicaBucla() {
  useT();
  return (
    <Pas titlu={t("struct.rzv.fundatia_are_doi")}
             rezumat={t("struct.rzv.rezumat_doi_pasi")}>
      <p>
        {t("struct.rzv.fundatia_structura_condi")}
      </p>
      <ul>
        <li>{tx("struct.rzv.li_pasul_6")}</li>
        <li>{tx("struct.rzv.li_pasii_7_12")}</li>
        <li>{tx("struct.rzv.b_pasul_17")}</li>
      </ul>
      <p>{tx("struct.rzv.bucla_nu_reruleaza")}</p>
      <p>{tx("struct.rzv.butonul_ruleaza_tot")}</p>
    </Pas>
  );
}

// Editorul coeficientului de excentricitate al unui ax: arată situația
// DETECTATĂ din geometrie, plaja și valoarea folosită, și lasă structuristul
// să o suprascrie per ax (preferința «coef_excentricitate_pe_ax»). Valoarea
// nouă intră în calcul la următoarea rulare a Pasului 17.
function CoefExcEditor({ projectId, ax }: { projectId: string; ax: any }) {
  useT();
  const [valoare, setValoare] = useState<string>("");
  const [stare, setStare] = useState<string | null>(null);
  // Editorul lui k_exc scrie în preferințele proiectului (`savePrefs`).
  const { potScrie } = useRolProiect();

  useEffect(() => {
    setValoare("");
    setStare(null);
  }, [ax?.nume]);

  const salveaza = async (sterge = false) => {
    try {
      const prefs = await getPrefs(projectId);
      const d: { [k: string]: number } =
        { ...(prefs.coef_excentricitate_pe_ax ?? {}) };
      if (sterge) {
        delete d[ax.nume];
      } else {
        const v = parseFloat(valoare.replace(",", "."));
        if (!isFinite(v) || v < 1.0 || v > 2.0) {
          setStare(t("struct.rzv.valoare_invalida"));
          return;
        }
        d[ax.nume] = v;
      }
      // DOAR felia ecranului: endpointul face merge peste rândul salvat, iar
      // obiectul întreg de la `getPrefs` (implicitele plus rândul) ar scrie
      // fiecare implicit ca alegere a omului.
      await savePrefs(projectId, { coef_excentricitate_pe_ax: d });
      setStare(t(sterge ? "struct.rzv.revenit_implicit"
                        : "struct.rzv.salvat_reruleaza"));
    } catch (e: any) {
      setStare(t("struct.rzv.eroare_salvare", { motiv: e?.message ?? e }));
    }
  };

  const plaja = ax.coef_excentricitate_plaja;
  return (
    <div className="vs-na" style={{ margin: "8px 0" }}>
      <strong>{t("struct.rzv.coef_exc_egal", { k: n(ax.coef_excentricitate, 2) })}</strong>
      {" "}<span className="sg-muted">({ax.coef_excentricitate_sursa})</span>
      <Info
        titlu={t("struct.rzv.acopera_coeficientul_exc")}
        ce={t("struct.rzv.modelul_asaza_talpa")}
        deCe={t("struct.rzv.ia_acoperitor_ax")}
        parametri={[
          { simbol: t("struct.rzv.sim_situatia"),
            nume: ax.situatie_excentricitate_eticheta ?? "—",
            sursa: ax.situatie_excentricitate_detaliu
              ?? t("struct.rzv.detectare_geometrie") },
          { simbol: t("struct.rzv.sim_plaja"),
            nume: plaja ? `${plaja[0]}–${plaja[1]}` : "—",
            sursa: t("struct.rzv.practica_birou") },
          { simbol: t("struct.rzv.sim_implicit"), nume: `${ax.coef_excentricitate_implicit ?? "—"}`,
            sursa: t("struct.rzv.fundatii_coef_excentrici") },
        ]}
      />
      <div className="sg-muted" style={{ margin: "4px 0" }}>
        {tx("struct.rzv.situatia_detectata", {
          eticheta: ax.situatie_excentricitate_eticheta,
          detaliu: ax.situatie_excentricitate_detaliu,
        })}
        {plaja && t("struct.rzv.plaja_implicit", {
          min: plaja[0], max: plaja[1],
          implicit: ax.coef_excentricitate_implicit,
        })}
      </div>
      {potScrie && (
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <label className="sg-muted">{t("struct.rzv.valoare_per_ax")}</label>
        <input style={{ width: 80 }} placeholder={`${ax.coef_excentricitate ?? ""}`}
               value={valoare} onChange={(e) => setValoare(e.target.value)} />
        <button className="vs-jump" onClick={() => salveaza(false)}>{t("struct.rzv.salveaza")}</button>
        <button className="vs-jump" onClick={() => salveaza(true)}>{t("struct.rzv.revino_implicit")}</button>
        {stare && <span className="sg-muted">{stare}</span>}
      </div>
      )}
    </div>
  );
}

// Un număr cu ZECIMALE FIXE, în convenția limbii: 12,50 în română, 12.50 în
// engleză. `toFixed` scrie mereu punct, iar într-o foaie de calcul de fundații
// punctul citit ca separator de mii schimbă ordinul de mărime.
function n(v: number | null | undefined, d = 1) {
  return numar(v, { minimumFractionDigits: d, maximumFractionDigits: d });
}

type Tab = "axe" | "stalpi" | "global" | "grinzi" | "model" | "cautare" | "metoda";

// Ținta de navigare venită din afară (indicatorul de verificări sau desenul
// 2D): tabul + elementul de preselectat. `n` e un contor care crește la
// fiecare „jump to", ca aceeași țintă cerută de două ori să reaplice focusul.
export type FocusTab = Tab;
export interface Focus {
  tab: FocusTab;
  ref: string | null;
  n: number;
}

function fmtVal(v: VerificareSumar, cheie: "valoare" | "prag") {
  const x = v[cheie];
  if (x == null) return "—";
  return `${x.toFixed(v.zecimale)}${v.unitate ? ` ${v.unitate}` : ""}`;
}

// Contorul din capul Pasului 17: câte verificări sunt și cum stau. Tabelele de
// mai jos arată câte un „✓/✗" per celulă, dar pe un proiect ies câteva sute
// și nimeni nu le numără cu ochiul — exact așa a scăpat o depășire pe un ax.
function IndicatorVerificari({
  v,
  sari,
}: {
  v: VerificariFundatie;
  sari?: (tab: FocusTab, ref: string | null,
          unde?: "verificari" | "desen") => void;
}) {
  useT();
  const s = v.sumar;
  const [deschis, setDeschis] = useState(true);
  if (!s) return null;
  const picate = s.picate.filter((p) => p.ok === false);
  const na = s.picate.filter((p) => p.ok === null);
  const toateTrec = picate.length === 0;
  const praguri = {
    ambele: t("struct.rzv.praguri_ambele"),
    metoda: t("struct.rzv.praguri_metoda"),
    np112: t("struct.rzv.praguri_np112"),
  }[s.praguri_obligatorii];

  return (
    <div className={`vs-bar ${toateTrec ? "vs-ok" : "vs-bad"}`}>
      <div className="vs-head">
        <span className="vs-total">
          <strong>{s.nr_total}</strong> {t("struct.rzv.verificari")}
        </span>
        <span className="vs-chip vs-chip-ok">✓ {t("struct.rzv.n_trec", { n: s.nr_trec })}</span>
        <span className={`vs-chip ${picate.length ? "vs-chip-bad" : "vs-chip-mut"}`}>
          ✗ {t("struct.rzv.n_nu_trec", { n: picate.length })}
        </span>
        {na.length > 0 && (
          <span className="vs-chip vs-chip-mut">— {t("struct.rzv.n_neaplicabile", { n: na.length })}</span>
        )}
        {s.nr_divergente_prag > 0 && (
          <span className="vs-chip vs-chip-warn">
            ⚠ {t("struct.rzv.verdicte_divergente", { n: s.nr_divergente_prag })}
          </span>
        )}
        <Info
          titlu={t("struct.rzv.numara_indicatorul")}
          ce={t("struct.rzv.celula_tabelele_jos")}
          deCe={t("struct.rzv.proiect_obisnuit_ies")}
          parametri={[
            { simbol: t("struct.rzv.sim_praguri"), nume: praguri,
              sursa: t("struct.rzv.praguri_sursa") },
            { simbol: t("struct.rzv.sim_urmarite"),
              nume: t("struct.rzv.verificarile_bucla_pasul"),
              sursa: t("struct.rzv.catalogul_actiuni_f1") },
          ]}
        />
        <span className="sg-spacer" />
        {(picate.length > 0 || na.length > 0) && (
          <button className="vs-toggle" onClick={() => setDeschis(!deschis)}>
            {t(deschis ? "struct.rzv.ascunde_lista" : "struct.rzv.arata_lista")}
          </button>
        )}
      </div>

      <div className="vs-grupe">
        {s.grupe.map((g) => (
          <button key={g.grup} className="vs-grup"
                  onClick={() => sari?.(g.tab as FocusTab, null)}>
            <span className="vs-grup-nume">{g.grup}</span>
            <span className="vs-grup-nr">{g.nr_total}</span>
            <span className={g.nr_pica ? "st-bad" : "st-ok"}>
              {g.nr_pica ? `${g.nr_pica} ✗` : "✓"}
            </span>
            {g.nr_neaplicabil > 0 && (
              <span className="sg-muted">{g.nr_neaplicabil} —</span>
            )}
          </button>
        ))}
      </div>

      {toateTrec && (
        <div className="vs-verdict st-ok">
          {t("struct.rzv.toate_trec_geometria", { praguri })}
        </div>
      )}

      {/* de ce mai sunt verificări picate DUPĂ ce bucla locală a iterat: fără
          asta, motivul stă ascuns în tabul «Căutarea geometriei», iar pasul
          pare că pur și simplu nu a încercat */}
      {!toateTrec && v.cautare?.activ && (
        <div className="vs-verdict">
          {v.cautare.convergent === false ? (
            <>
              <strong className="st-bad">{t("struct.rzv.bucla_oprita_fara")}</strong>{" "}
              {t("struct.rzv.dupa_iteratii", {
                n: v.cautare.iteratii ?? 0, oprire: v.cautare.oprire,
              })}{" "}
              {tx("struct.rzv.urmeaza_depaseste")}
              {sari && (
                <button className="vs-jump" style={{ marginLeft: 8 }}
                        onClick={() => sari("cautare", null)}>
                  {t("struct.rzv.geometria_gasita")}
                </button>
              )}
            </>
          ) : picate.every((p) => !p.urmarit) ? (
            <>
              {tx("struct.rzv.bucla_rezolvat_tot", {
                n: v.cautare.iteratii ?? 0,
                pas: t("struct.rzv.reluarea_lantului_pasul"),
              })}
            </>
          ) : (
            <>{t("struct.rzv.bucla_pasului_6")}</>
          )}
        </div>
      )}
      {!toateTrec && v.cautare && !v.cautare.activ && (
        <div className="vs-verdict sg-muted">
          {t("struct.rzv.bucla_oprita_manual", { motiv: traduceRezultat(v.cautare.motiv) })}
        </div>
      )}

      {deschis && picate.length > 0 && (
        <table className="sg-table vs-lista">
          <thead>
            <tr>
              <th></th><th>{t("struct.rzv.unde")}</th>
              <th>{t("struct.rzv.col_verificarea")}</th>
              <th>{t("struct.rzv.col_valoare")}</th>
              <th>{t("struct.rzv.col_prag")}</th>
              <th>{t("struct.rzv.referinta")}</th><th></th>
            </tr>
          </thead>
          <tbody>
            {picate.map((p, i) => (
              <tr key={`${p.cod}-${p.unde}-${i}`}>
                <td className="st-bad">✗</td>
                <td><strong>{p.unde}</strong></td>
                <td>
                  {p.nume}
                  {p.detaliu && <div className="sg-muted">{p.detaliu}</div>}
                  {!p.urmarit && (
                    <div className="sg-muted">{t("struct.rzv.nu_e_in_catalog")}</div>
                  )}
                </td>
                <td className="st-bad">{fmtVal(p, "valoare")}</td>
                <td>{p.inversat ? "≥ " : "≤ "}{fmtVal(p, "prag")}</td>
                <td className="sg-muted">{p.referinta}</td>
                <td>
                  {sari && (
                    <button className="vs-jump"
                            onClick={() => sari(p.tab as FocusTab, p.ref)}>
                      {t("struct.rzv.vezi")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {deschis && na.length > 0 && (
        <div className="vs-na sg-muted">
          {t("struct.rzv.neaplicabile")}{" "}
          {na.map((p) => t("struct.rzv.p_unde_nume",
                           { unde: p.unde, nume: p.nume })).join(" · ")}
        </div>
      )}
    </div>
  );
}

export default function StepRezistentaVerificari({
  projectId,
  cloneId = null,
  r,
  focus,
  sari,
}: {
  projectId: string;
  // clona soluției vizualizate din care se citește CSV-ul; null = proiectul
  cloneId?: string | null;
  r: RezistentaResult;
  focus?: Focus;
  sari?: (tab: FocusTab, ref: string | null,
          unde?: "verificari" | "desen") => void;
}) {
  useT();
  const v: VerificariFundatie | undefined = r.verificari;
  // titlurile verificărilor: ACELAȘI catalog ca la Pașii 10, 13, 14, 15, 16
  const catalog = useCatalogVerificari();
  const [tab, setTab] = useState<Tab>("axe");
  const [axDetaliu, setAxDetaliu] = useState<string | null>(null);
  const [stalpFocus, setStalpFocus] = useState<string | null>(null);
  const [grindaFocus, setGrindaFocus] = useState<string | null>(null);

  // focusul cerut din afară se aplică pe tab + elementul potrivit
  useEffect(() => {
    if (!focus) return;
    setTab(focus.tab);
    if (focus.tab === "axe") setAxDetaliu(focus.ref);
    if (focus.tab === "stalpi") setStalpFocus(focus.ref);
    if (focus.tab === "grinzi") setGrindaFocus(focus.ref);
  }, [focus?.n, focus?.tab, focus?.ref]);

  if (!v) return null;

  if (v.stare !== "calculat") {
    return (
      <div className="st-findings">
        <h3>{t("struct.rzv.nu_s_a_facut")}</h3>
        <div className="st-warn">⏳ {v.motiv}</div>
        <p className="sg-muted">
          {t("struct.rzv.pasul_6_predimensionarea")}
        </p>
        <ExplicaBucla />
      </div>
    );
  }

  const axe = v.axe ?? [];
  const stalpi = v.stalpi ?? [];
  const g = v.global ?? {};
  const grinzi = v.grinzi_fundare ?? [];
  const axSel = axe.find((a) => a.nume === axDetaliu);

  const taburi: [Tab, string, number | null][] = [
    ["axe", t("struct.rzv.tab_axe"), axe.filter((a) => a.noduri?.length).length],
    ["stalpi", t("struct.rzv.stalpi_lamele2"), stalpi.length],
    ["global", t("struct.rzv.tab_global"), null],
    ["grinzi", t("struct.rzv.tab_grinzi"), grinzi.length],
    ["model", t("struct.rzv.tab_model"), null],
    ["cautare", t("struct.rzv.cautarea_geometriei"), v.cautare?.iteratii ?? null],
    ["metoda", t("struct.rzv.metoda_vs_normativ"),
     v.avertismente_metoda?.length ?? 0],
  ];

  return (
    <div className="st-findings">
      <h3>
        {t("struct.rzv.verificarea_reactiuni_reale")}
        <span className="sg-muted">
          {" "}
          {t("struct.rzv.comb_x_axe", {
            c: v.nr_combinatii,
            a: axe.filter((a) => a.noduri?.length).length,
          })}
        </span>
        <Info
          titlu={t("struct.rzv.verifica_per_ax")}
          ce={t("struct.rzv.predimensionarea_imparte")}
          deCe={t("struct.rzv.structura_foilor_calcul")}
          parametri={[
            { simbol: "ΣF_Z, ΣM", nume: t("struct.rzv.suma_reactiunilor_noduri"), sursa: t("struct.rzv.tensorul_combinat_pasulu") },
            { simbol: "A = L·B", nume: t("struct.rzv.aria_talpii_ax"), sursa: t("struct.rzv.l_partea_acoperita") },
            { simbol: "W", nume: t("struct.rzv.modulele_rezistenta_b"), sursa: "06.predimensionare fundatii" },
          ]}
        />
      </h3>

      <IndicatorVerificari v={v} sari={sari} />

      <div className="md-tabs">
        {taburi.map(([k, eticheta, nr]) => (
          <button key={k} className={`md-tab ${tab === k ? "active" : ""}`}
                  onClick={() => setTab(k)}>
            {eticheta}{nr != null && nr > 0 ? ` (${nr})` : ""}
          </button>
        ))}
        <a className="md-tab" href={fundatiiCsvUrl(projectId, cloneId)}>
          {t("struct.rzv.csv_per_ax")}
        </a>
      </div>

      {/* ---------------------------------------------------------------- AXE */}
      {tab === "axe" && (
        <>
          <Pas titlu={t("struct.rzv.ax_fundatie_verifica")}
             rezumat={t("struct.rzv.ax_fundatie_verifica2")}>
            <p>{tx("struct.rzv.panglica_fundatie_verifi")}</p>
            <p>
              {t("struct.rzv.motivul_axele_egale")}
            </p>
            <p>{tx("struct.rzv.ax_primeste_b")}</p>
          </Pas>
          <div className="dg-grid">
            <D.DiagramaPresiuni />
            <D.DiagramaWinkler />
          </div>
          <Legenda
            intro={t("struct.rzv.click_rand_deschide2")}
            coloane={[
              {
                nume: t("struct.rzv.th_ax"),
                ce: <>{t("struct.rzv.numele_axului_literele")}</>,
                sursa: t("struct.rzv.pasul_5_segmentele"),
              },
              {
                nume: "Dir.",
                ce: <>{tx("struct.rzv.col_dir_ce")}</>,
                formula: t("struct.rzv.col_dir_formula"),
              },
              {
                nume: "L [m]",
                ce: <>{tx("struct.rzv.col_l_ce")}</>,
                formula: t("struct.rzv.col_l_formula"),
                validare: <>{t("struct.rzv.col_l_validare")}</>,
              },
              {
                nume: "B [m]",
                ce: <>{tx("struct.rzv.col_b_ce", { b: n(r.latime_m, 2) })}</>,
                formula: t("struct.rzv.col_b_formula"),
                validare: <>{t("struct.rzv.col_b_validare",
                               { min: n(r.latime_minima_m, 2) })}</>,
                sursa: t("struct.rzv.decizia_pasului_6"),
              },
              {
                nume: "k_exc",
                ce: <>{tx("struct.rzv.col_kexc_ce")}</>,
                formula: t("struct.rzv.col_kexc_formula"),
                validare: <>{tx("struct.rzv.col_kexc_validare")}</>,
                sursa: t("struct.rzv.decizie_proiectare_impli"),
              },
              {
                nume: "H / Df",
                ce: <>{tx("struct.rzv.col_h_ce", {
                  h: n(r.h_grinda_m, 2), df: n(r.adancime_m, 2),
                  sectiune: r.sectiune_descriere ?? "B × H" })}</>,
                formula: t(r.detaliu_fundatie === "T" ? "struct.rzv.h_detaliu_t"
                  : r.detaliu_fundatie === "talpa" ? "struct.rzv.h_cu_subsol"
                  : "struct.rzv.h_min"),
                validare: <>{tx("struct.rzv.col_h_validare")}</>,
                sursa: t("struct.rzv.pasul_6_detaliul"),
              },
              {
                nume: t("struct.rzv.th_acoperire"),
                ce: <>{t("struct.rzv.anvelopa_axului_chiar")}</>,
                formula: t("struct.rzv.col_acop_formula"),
                validare: <>{tx("struct.rzv.col_acop_validare")}</>,
              },
              {
                nume: t("struct.rzv.stalpi_lamele"),
                ce: <>{t("struct.rzv.cate_noduri_reazem")}</>,
                validare: <>{tx("struct.rzv.validare_stalp_aflat_intersectia")}</>,
              },
              {
                nume: t("struct.rzv.guverneaza"),
                ce: <>{t("struct.rzv.combinatia_incarcari_pro")}</>,
                sursa: t("struct.rzv.pasul_11_comb", { n: v.nr_combinatii }),
              },
              {
                nume: t("struct.rzv.p_metoda"),
                ce: <>{t("struct.rzv.presiunea_maxima_margine")}</>,
                formula: "p₁ = ΣF_Z/A + ΣM_long/W_long + ΣM_transv/W_transv\nA = L·B,  W_long = B·L²/6,  W_transv = L·B²/6",
                validare: <>{tx("struct.rzv.validare_p1_b_1", { p1: n(r.prag_static_kpa), p2: n(r.prag_dinamic_kpa) })}</>,
              },
              {
                nume: "p NP 112",
                ce: <>{tx("struct.rzv.ce_aceeasi_presiune_maxima", { p1: t("struct.rzv.terenul_poate_trage") })}</>,
                formula: t("struct.rzv.formula_p_np112"),
                validare: <>{tx("struct.rzv.validare_pragul_depinde_tipul")}</>,
              },
              {
                nume: "p model",
                ce: <>{tx("struct.rzv.ce_presiunea_citita_b")}</>,
                formula: t("struct.rzv.formula_resort"),
                validare: <>{tx("struct.rzv.validare_acelasi_prag_metoda")}</>,
              },
              {
                nume: t("struct.rzv.col_tasare"),
                ce: <>{t("struct.rzv.coboara_terenul_sub")}</>,
                formula: t("struct.rzv.formula_tasare"),
                validare: <>{tx("struct.rzv.validare_diferenta_dintre_axe")}</>,
              },
            ]}
          />
          {(v.cautare as any)?.cuantizare_latimi?.activa && (() => {
            const q = (v.cautare as any).cuantizare_latimi;
            return (
              <Pas titlu={t("struct.rzv.latimi_talpii", { n: q.nivele_m?.length ?? 0 })}>
                <p>
                  {tx("struct.rzv.latimi_rotunjite", {
                    n: q.max_nivele,
                    lista: (q.nivele_m ?? []).map((x: number) => n(x, 2)).join(" / "),
                  })}
                </p>
                <p className="sg-muted">
                  {t("struct.rzv.b_necesar_per_ax", {
                    delta: `${q.delta_cost_eur > 0 ? "+" : ""}${q.delta_cost_eur}`,
                    detaliu: (q.per_ax ?? []).map((x: any) =>
                      t("struct.rzv.per_ax_necesar_ales", {
                        ax: x.ax, nec: n(x.B_necesar_m, 2), ales: n(x.B_ales_m, 2),
                      })
                    ).join(" · "),
                  })}
                </p>
              </Pas>
            );
          })()}
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.rzv.th_ax")}</th><th>{t("struct.rzv.th_dir")}</th>
                <th>{t("struct.rzv.th_l_m")}</th>
                <th>{t("struct.rzv.th_b_m")}</th>
                <th>{t("struct.rzv.th_excentricitate")}</th><th>k_exc</th>
                <th>{t("struct.rzv.th_h_df")}</th><th>{t("struct.rzv.th_acoperire")}</th>
                <th>{t("struct.rzv.stalpi")}</th><th>{t("struct.rzv.guverneaza")}</th>
                <th>
                  <TitluVerificare catalog={catalog} tip="fundatie_presiune"
                                   text={t("struct.rzv.p_metoda_kpa")} aratPasul={false}
                                   aratNumele={false} />
                </th><th></th>
                <th>
                  <TitluVerificare catalog={catalog} tip="fundatie_presiune"
                                   text="p NP 112 [kPa]" aratPasul={false}
                                   aratNumele={false} />
                </th><th></th>
                <th>
                  <TitluVerificare catalog={catalog} tip="fundatie_presiune"
                                   text="p model [kPa]" aratPasul={false}
                                   aratNumele={false} />
                </th><th></th>
                <th>{t("struct.rzv.th_tasare_cm")}</th>
              </tr>
            </thead>
            <tbody>
              {axe.filter((a) => a.noduri?.length).map((a) => {
                const guv = a.combinatii?.find((c) => c.combinatie === a.guverneaza);
                return (
                  <tr key={a.nume}
                      className={`am-row ${axDetaliu === a.nume ? "vs-focus" : ""}`}
                      onClick={() => setAxDetaliu(axDetaliu === a.nume ? null : a.nume)}>
                    <td>
                      <strong>{a.nume}</strong>
                      {sari && (
                        <button className="vs-jump vs-jump-mic"
                                title={t("struct.rzv.arata_axul_desenul")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sari("axe", a.nume, "desen");
                                }}>
                          ⌖
                        </button>
                      )}
                    </td>
                    <td>{a.directie}</td>
                    <td>{n(a.L_m, 2)}</td>
                    {/* grosimea tălpii pe ax — pe desen e banda galbenă */}
                    <td title={a.latit_local
                          ? t("struct.rzv.latit_local", { b: n(r.latime_m, 2) })
                          : t("struct.rzv.latimea_globala")}>
                      <strong>{n(a.B_m ?? r.latime_m, 2)}</strong>
                      {a.latit_local && <span className="sg-muted"> {t("struct.rzv.latit")}</span>}
                    </td>
                    {/* excentricitatea REALĂ, din eforturi: centric / pe o direcție /
                        pe două. Ea alege pragul NP 112 tab. I.5 al axului —
                        cerința structuristului lângă lățime. */}
                    <td title={a.excentricitate?.explicatie ?? t("struct.rzv.fara_reactiuni")}>
                      {a.excentricitate ? (
                        <>
                          <strong className={a.excentricitate.nr_directii === 2
                                ? "st-warn" : ""}>
                            {t(a.excentricitate.nr_directii === 0 ? "struct.rzv.centric"
                              : a.excentricitate.nr_directii === 1 ? "struct.rzv.o_directie"
                              : "struct.rzv.doua_directii2")}
                          </strong>
                          {a.excentricitate.tip_guvernant
                             !== a.excentricitate.tip_maxim && (
                            <span className="sg-muted">
                              {" "}{t("struct.rzv.guv_paren", { tip: a.excentricitate.tip_guvernant })}
                            </span>
                          )}
                        </>
                      ) : <span className="sg-muted">—</span>}
                    </td>
                    <td title={`${a.situatie_excentricitate_eticheta ?? ""} — ${a.coef_excentricitate_sursa ?? ""}`}>
                      {n(a.coef_excentricitate, 2)}
                      {String(a.coef_excentricitate_sursa ?? "").includes("preferință")
                        && <span className="sg-muted"> {t("struct.rzv.editat")}</span>}
                    </td>
                    <td className="sg-muted">
                      {n(r.h_grinda_m, 2)} / {n(r.adancime_m, 2)}
                    </td>
                    <td className={(a.acoperire_pct ?? 100) < 70 ? "st-bad" : ""}>
                      {n(a.acoperire_pct, 0)}%
                    </td>
                    <td>{a.nr_stalpi}</td>
                    <td className="sg-muted">{a.guverneaza}</td>
                    <td>{n(a.p_max_kpa)}</td>
                    <td>
                      <Verdict ok={a.ok_metoda} valoare={a.p_max_kpa}
                               prag={guv?.prag_metoda_kpa} unitate="kPa"
                               ce={t("struct.rzv.v_p1_ce", { comb: a.guverneaza })}
                               regula={t("struct.rzv.v_p1_regula", {
                                 mult: n(guv?.grupa === "fundamentala" ? 1.1 : 1.35, 2),
                                 p: n(r.p_conv_kpa),
                               })}
                               referinta={t("struct.rzv.ref_metoda_02")} />
                    </td>
                    <td>{n(guv?.p_max_np112_kpa)}</td>
                    <td>
                      <Verdict ok={a.ok_np112} valoare={guv?.p_max_np112_kpa}
                               prag={guv?.prag_np112_kpa} unitate="kPa"
                               ce={t("struct.rzv.v_np_ce", { mu: n(guv?.mu, 3) })}
                               regula={t("struct.rzv.v_np_regula", {
                                 tip: guv?.tip_excentricitate,
                                 grupa: t(guv?.grupa === "fundamentala"
                                   ? "struct.rzv.fundamentala" : "struct.rzv.seismica"),
                               })}
                               referinta={t("struct.rzv.ref_np112_i5_anexa_e")} />
                    </td>
                    <td>{n(a.p_max_model_kpa)}</td>
                    <td>
                      <Verdict ok={a.ok_model} valoare={a.p_max_model_kpa}
                               prag={guv?.prag_metoda_kpa} unitate="kPa"
                               ce={t("struct.rzv.v_model_ce")}
                               regula={t("struct.rzv.v_model_regula", {
                                 mult: n(guv?.grupa === "fundamentala" ? 1.1 : 1.35, 2),
                               })}
                               referinta={t("struct.rzv.presiune_resoarte")} />
                    </td>
                    <td>
                      {n(a.tasare_cm, 2)}{" "}
                      <Verdict ok={(a.tasare_cm ?? 0) <= (g.tasare_absoluta_limita_cm ?? 8)}
                               valoare={a.tasare_cm} prag={g.tasare_absoluta_limita_cm}
                               unitate="cm" zecimale={2}
                               ce={t("struct.rzv.v_tasare_ce")}
                               regula={t("struct.rzv.v_tasare_regula")}
                               referinta="NP 112-2014 tab. H.1" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {axSel && (
            <>
              <h4 style={{ marginTop: 14 }}>
                {t("struct.rzv.ax_toate_combinatiile", { ax: axSel.nume })}
                <span className="sg-muted">
                  {t("struct.rzv.talpa_detalii", {
                    b: n(axSel.B_m ?? r.latime_m, 2), h: n(r.h_grinda_m, 2),
                    d: n(r.adancime_m, 2), l: n(axSel.L_m, 2), a: n(axSel.A_m2, 2),
                    wl: n(axSel.W_long_m3, 2), wt: n(axSel.W_transv_m3, 3),
                  })}
                </span>
              </h4>
              <CoefExcEditor projectId={projectId} ax={axSel} />
              {(axSel.coef_excentricitate ?? 1) > 1 && (
                <div className="sg-muted" style={{ marginBottom: 6 }}>
                  {t("struct.rzv.presiunile_includ_kexc", {
                    k: n(axSel.coef_excentricitate, 2),
                  })}
                </div>
              )}
              <TabelCombinatiiAx ax={axSel} />
            </>
          )}
        </>
      )}

      {/* ------------------------------------------------------------- STÂLPI */}
      {tab === "stalpi" && (
        <>
          <Pas titlu={t("struct.rzv.presiunea_pune_stalp")}
             rezumat={t("struct.rzv.verificarea_varfului")}>
            <p>{tx("struct.rzv.verificarea_ax_fasia")}</p>
            <p>{tx("struct.rzv.strapungerea_cedarea")}</p>
            <p>
              {t("struct.rzv.forta_stalpului_scade")}
            </p>
          </Pas>
          <D.DiagramaStrapungere />
          <Legenda
            coloane={[
              {
                nume: t("struct.rzv.stalp"),
                ce: <>{t("struct.rzv.identificatorul_planul_c")}</>,
                sursa: t("struct.rzv.pasul_4_id"),
              },
              {
                nume: "N_max",
                ce: <>{t("struct.rzv.cea_mare_forta")}</>,
                sursa: t("struct.rzv.reactiunea_nodului_tenso"),
              },
              {
                nume: "l_afer",
                ce: <>{t("struct.rzv.lungimea_talpa_tine")}</>,
                formula: t("struct.rzv.lungimea_aferenta_resort"),
              },
              {
                nume: "p",
                ce: <>{t("struct.rzv.apasarea_locala_teren")}</>,
                formula: "p = N_max / (B · l_afer)",
                validare: <>{tx("struct.rzv.validare_acelasi_prag_ax")}</>,
              },
              {
                nume: t("struct.rzv.pozitie"),
                ce: <>{t("struct.rzv.interior_margine_conteaz")}</>,
                formula: t("struct.rzv.margine_daca"),
              },
              {
                nume: "v_Ed",
                ce: <>{t("struct.rzv.efortul_forfecare_apare")}</>,
                formula: t("struct.rzv.formula_strapungere"),
              },
              {
                nume: "v_Rd,c",
                ce: <>{t("struct.rzv.poate_duce_betonul")}</>,
                formula: "v_Rd,c = 0,12·k·(100·ρ₁·f_ck)^⅓,  k = 1+√(0,2/d) ≤ 2",
                sursa: "SR EN 1992-1-1 §6.4 / NP 112 §II.6.1.1(1)c",
              },
              {
                nume: "R/E",
                ce: <>{t("struct.rzv.rezerva_cate_ori")}</>,
                formula: "R/E = v_Rd,c / v_Ed",
                validare: <>{tx("struct.rzv.validare_b_1_00")}</>,
              },
            ]}
          />
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.rzv.stalp")}</th><th>{t("struct.rzv.th_xy")}</th>
                <th>N_max [kN]</th><th>{t("struct.rzv.combinatie")}</th>
                <th>l_afer [m]</th>
                <th>
                  <TitluVerificare catalog={catalog}
                                   tip="fundatie_presiune_stalp"
                                   text="p [kPa]" aratPasul={false}
                                   aratNumele={false} />
                </th><th>{t("struct.rzv.th_prag")}</th><th></th>
                <th>{t("struct.rzv.pozitie")}</th>
                <th>
                  <TitluVerificare catalog={catalog}
                                   tip="fundatie_strapungere"
                                   text="v_Ed [kPa]" aratPasul={false}
                                   aratNumele={false} />
                </th><th>v_Rd,c [kPa]</th><th>R/E</th><th></th>
              </tr>
            </thead>
            <tbody>
              {stalpi.map((s) => {
                // referința de focus rămâne cea a serverului („nod 12"); pe
                // ecran se scrie în limba curentă
                const eticheta = s.id_plan ?? `nod ${s.nod}`;
                return (
                <tr key={s.nod}
                    className={stalpFocus === eticheta ? "vs-focus" : ""}>
                  <td><strong>{s.id_plan ?? t("struct.rzv.nod_n", { n: s.nod })}</strong></td>
                  <td className="sg-muted">{n(s.x_m, 2)}, {n(s.y_m, 2)}</td>
                  <td>{n(s.N_max_kn)}</td>
                  <td className="sg-muted">{s.combinatie}</td>
                  <td>{n(s.lungime_aferenta_m, 2)}</td>
                  <td>{n(s.p_stalp_kpa)}</td>
                  <td>{n(s.prag_kpa)}</td>
                  <td>
                    <Verdict ok={s.ok_presiune} valoare={s.p_stalp_kpa}
                             prag={s.prag_kpa} unitate="kPa"
                             ce={t("struct.rzv.v_p_stalp_ce", {
                               l: n(s.lungime_aferenta_m, 2), b: n(r.latime_m, 2),
                             })}
                             regula={t("struct.rzv.v_p_stalp_regula", {
                               mult: n(s.grupa === "fundamentala" ? 1.1 : 1.35, 2),
                             })}
                             referinta={t("struct.rzv.ref_metoda_np112")} />
                  </td>
                  <td className="sg-muted">{t(s.marginal
                    ? "struct.rzv.margine" : "struct.rzv.interior")}</td>
                  <td>{n(s.strapungere.v_Ed_kpa)}</td>
                  <td>{n(s.strapungere.v_Rd_c_kpa)}</td>
                  <td>{s.strapungere.raport == null ? "—" : (1 / s.strapungere.raport).toFixed(2)}</td>
                  <td>
                    <Verdict ok={s.strapungere.ok} valoare={s.strapungere.v_Ed_kpa}
                             prag={s.strapungere.v_Rd_c_kpa} unitate="kPa"
                             ce={t("struct.rzv.v_strapungere_ce")}
                             regula={t("struct.rzv.v_strapungere_regula", {
                               beta: s.strapungere.beta,
                               poz: t(s.marginal
                                 ? "struct.rzv.de_margine" : "struct.rzv.interior"),
                               nred: n(s.strapungere.N_red_kn),
                             })}
                             referinta="SR EN 1992-1-1 §6.4 / NP 112 §II.6.1.1(1)c" />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* ------------------------------------------------------------- GLOBAL */}
      {tab === "global" && (
        <>
        <Pas titlu={t("struct.rzv.verificarile_privesc_fun")}
             rezumat={t("struct.rzv.global_toata_cladirea2")}>
          <p>{tx("struct.rzv.pana_aici_bucata")}</p>
          <p>
            {t("struct.rzv.card_arata_raportul")}
          </p>
        </Pas>
        <div className="st-cards">
          <div className="st-card">
            <div className="st-card-label">
              <TitluVerificare catalog={catalog} tip="fundatie_lunecare"
                               text={t("struct.rzv.lunecare_talpa")} aratPasul={false}
                               aratNumele={false} /> <Da ok={g.lunecare?.ok} />
            </div>
            <div className="st-card-value">
              {n(g.lunecare?.guverneaza?.raport, 2)}
            </div>
            <div className="st-card-sub">
              {t("struct.rzv.card_lunecare_sub", {
                comb: g.lunecare?.guverneaza?.combinatie,
                mu: g.lunecare?.coef_frecare,
                r: n(g.lunecare?.R_pasiv_kn),
              })}
            </div>
          </div>

          <div className="st-card">
            <div className="st-card-label">
              <TitluVerificare catalog={catalog} tip="fundatie_rasturnare"
                               text={t("struct.rzv.rasturnare")} aratPasul={false}
                               aratNumele={false} /> <Da ok={g.rasturnare?.ok} />
            </div>
            <div className="st-card-value">{n(g.rasturnare?.guverneaza?.raport, 2)}</div>
            <div className="st-card-sub">
              {t("struct.rzv.card_rasturnare_sub", {
                comb: g.rasturnare?.guverneaza?.combinatie,
                brat: n(g.rasturnare?.guverneaza?.brat_m, 2),
                z: n(g.rasturnare?.z_cm_m, 2),
              })}
            </div>
          </div>

          <div className="st-card">
            <div className="st-card-label">
              <TitluVerificare catalog={catalog} tip="fundatie_subpresiune"
                               text={t("struct.rzv.subpresiune_plutire")} aratPasul={false}
                               aratNumele={false} /> <Da ok={g.subpresiune?.ok} />
            </div>
            <div className="st-card-value">
              {g.subpresiune?.aplicabil ? n(g.subpresiune?.raport, 2) : "n/a"}
            </div>
            <div className="st-card-sub">{g.subpresiune?.motiv ?? g.subpresiune?.nota}</div>
          </div>

          <div className="st-card">
            <div className="st-card-label">
              <TitluVerificare catalog={catalog} tip="fundatie_ridicare"
                               text={t("struct.rzv.ridicare_uplift")} aratPasul={false}
                               aratNumele={false} /> <Da ok={g.ridicare_ok} />
            </div>
            <div className="st-card-value">
              {(g.ridicare ?? []).filter((x: any) => !x.ok).length}
            </div>
            <div className="st-card-sub">
              {t("struct.rzv.stalpi_cu_tractiune", { total: (g.ridicare ?? []).length })}
            </div>
          </div>

          <div className="st-card">
            <div className="st-card-label">
              {t("struct.rzv.tasare_relativa2")}{" "}
              <EtichetaNatura cheie="tasare_relativa" />
              <Marime cheie="tasare_relativa" />{" "}
              <span className="vs-chip vs-chip-mut" title={t("struct.rzv.indicator_informativ_int")}>
                {t("struct.rzv.informativ")}
              </span>
              <Info
                titlu={t("struct.rzv.lasa_cladirea_neuniform")}
                ce={t("struct.rzv.conteaza_doar_lasa")}
                deCe={t("struct.rzv.np_112_tab")}
                parametri={[
                  { simbol: "Δs", nume: t("struct.rzv.diferenta_tasare_intre"), sursa: t("struct.rzv.tasarea_calculata_per") },
                  { simbol: "L", nume: t("struct.rzv.distanta_dintre_centrele"),
                    sursa: t("struct.rzv.geometria_pasului_5") },
                ]}
              />
            </div>
            <div className="st-card-value">
              {((g.tasare_relativa ?? 0) * 1000).toFixed(2)} <span className="st-small">‰</span>
            </div>
            <div className="st-card-sub">
              {t("struct.rzv.reper_orientativ", {
                v: n((g.tasare_relativa_limita ?? 0) * 1000, 1),
              })}
              {g.tasare_relativa_pereche && t("struct.rzv.intre_perechea", {
                a: g.tasare_relativa_pereche[0], b: g.tasare_relativa_pereche[1],
                l: g.tasare_relativa_pereche[2],
              })}
              {t("struct.rzv.tasare_absoluta_max", { v: n(g.tasare_absoluta_max_cm, 2) })}
            </div>
          </div>

          <div className="st-card">
            <div className="st-card-label">
              <TitluVerificare catalog={catalog} tip="fundatie_ks"
                               text={t("struct.rzv.bucla_teren_structura")} aratPasul={false}
                               aratNumele={false} /> <Da ok={g.ks?.ok} />
            </div>
            <div className="st-card-value">
              {n(g.ks?.abatere_pct, 1)} <span className="st-small">%</span>
            </div>
            <div className="st-card-sub">
              {t("struct.rzv.ks_model_cerut", {
                model: g.ks?.ks_predimensionare_dan_cm3 ?? "—",
                cerut: g.ks?.ks_recalculat_dan_cm3 ?? "—",
              })}
              {g.ks?.ks_predimensionare_sursa && (
                <div style={{ marginTop: 4 }}>
                  {t("struct.rzv.ks_vine_din", { sursa: g.ks.ks_predimensionare_sursa })}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="dg-grid">
          <D.DiagramaLunecare />
          <D.DiagramaRasturnare />
          <D.DiagramaSubpresiune />
          <D.DiagramaRidicare />
          <D.DiagramaTasare />
          <D.DiagramaTasareRelativa />
        </div>
        <Legenda
          titlu={t("struct.rzv.inseamna_verificare_glob")}
          coloane={[
            {
              nume: t("struct.rzv.lunecare_talpa"),
              ce: <>{t("struct.rzv.cutremurul_impinge_cladi")}</>,
              formula: t("struct.rzv.formula_lunecare"),
              validare: <>{tx("struct.rzv.validare_raportul_h_r", { p1: " " })}</>,
            },
            {
              nume: t("struct.rzv.rasturnare"),
              ce: <>{t("struct.rzv.forta_orizontala_aplicat")}</>,
              formula: t("struct.rzv.formula_rasturnare"),
              validare: <>{tx("struct.rzv.validare_raportul_m_stab")}</>,
            },
            {
              nume: t("struct.rzv.subpresiune_plutire"),
              ce: <>{t("struct.rzv.daca_panza_freatica")}</>,
              formula: t("struct.rzv.formula_subpresiune"),
              validare: <>{tx("struct.rzv.validare_ruleaza_b_p1", { p1: t("struct.rzv.doar") })}</>,
            },
            {
              nume: t("struct.rzv.ridicare_uplift"),
              ce: <>{t("struct.rzv.rasturnarea_ansamblu_sta")}</>,
              formula: t("struct.rzv.formula_ridicare"),
              validare: <>{tx("struct.rzv.validare_verificarea_locala_acope")}</>,
            },
            {
              nume: t("struct.rzv.tasare_relativa"),
              ce: <>{tx("struct.rzv.ce_conteaza_doar_lasa", { p1: " " })}</>,
              formula: t("struct.rzv.delta_s_l"),
              validare: <>{tx("struct.rzv.validare_b_0_002")}</>,
            },
            {
              nume: t("struct.rzv.bucla_teren_structura"),
              ce: <>{t("struct.rzv.coeficientul_pat_k")}</>,
              formula: t("struct.rzv.formula_ks_invers"),
              validare: <>{tx("struct.rzv.validare_abatere_b_20")}</>,
            },
          ]}
        />
        </>
      )}

      {/* ------------------------------------------------- GRINZI DE FUNDARE */}
      {tab === "grinzi" && (
        <>
          <Pas titlu={t("struct.rzv.grinda_fundare_armeaza")}
             rezumat={t("struct.rzv.reazema_teren_nu")}>
            <p>{tx("struct.rzv.grinda_planseu_pe_dos")}</p>
            <p>{tx("struct.rzv.grinda_pat_continuu")}</p>
            <p>{tx("struct.rzv.grinda_a_treia_diferenta")}</p>
          </Pas>
          <D.DiagramaGrindaFundare />
          <Legenda
            coloane={[
              {
                nume: t("struct.rzv.sectiune"),
                ce: <>{t("struct.rzv.b_h_c")}</>,
                formula: "d = H_c − c_sus − c_jos",
                sursa: t("struct.rzv.sursa_distanta_centre"),
              },
              {
                nume: "M⁺ / M⁻",
                ce: <>{t("struct.rzv.infasuratoarea_momentulu")}</>,
                formula: t("struct.rzv.max_min_statii"),
                sursa: t("struct.rzv.sursa_pasul_12_grinzi"),
              },
              {
                nume: "V",
                ce: <>{t("struct.rzv.cea_mare_forta2")}</>,
                formula: t("struct.rzv.max_v_z"),
              },
              {
                nume: t("struct.rzv.col_as_jos_sus"),
                ce: <>{t("struct.rzv.aria_armatura_longitudin")}</>,
                formula: "A_s = M / (0,9 · d · f_yd),  apoi A_s ≥ A_s,min",
                validare: <>{tx("struct.rzv.validare_o_minim_14")}</>,
              },
              {
                nume: "A_s min / ρ",
                ce: <>{t("struct.rzv.armatura_minima_construc")}</>,
                formula: t("struct.rzv.formula_rho_min"),
                sursa: "NP 112 §II.7.5.2",
              },
              {
                nume: "V_Rd,c",
                ce: <>{t("struct.rzv.duce_betonul_forfecare")}</>,
                formula: t("struct.rzv.formula_v_rd_c"),
                sursa: "SR EN 1992-1-1 §6.2.2",
              },
              {
                nume: t("struct.rzv.th_etrieri"),
                ce: <>{t("struct.rzv.daca_necesari_calcul")}</>,
                formula: t("struct.rzv.formula_etrieri"),
                validare: <>{tx("struct.rzv.validare_8_mm_etrieri")}</>,
              },
              {
                nume: t("struct.rzv.consola_talpa"),
                ce: <>{t("struct.rzv.talpa_lata_decat")}</>,
                formula: t("struct.rzv.formula_consola"),
                validare: <>{tx("struct.rzv.validare_0_10_directie", { p1: t("struct.rzv.grosimea_talpii") })}</>,
              },
            ]}
          />
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.rzv.th_ax")}</th><th>{t("struct.rzv.sectiune")}</th>
                <th>M⁺ [kNm]</th><th>M⁻ [kNm]</th><th>V [kN]</th>
                <th>{t("struct.rzv.th_as_jos")}</th><th>{t("struct.rzv.th_as_sus")}</th>
                <th>{t("struct.rzv.th_as_min")}</th>
                <th>ρ [%]</th><th>V_Rd,c [kN]</th><th>{t("struct.rzv.th_etrieri")}</th><th></th>
                <th>{t("struct.rzv.consola_talpa")}</th>
              </tr>
            </thead>
            <tbody>
              {grinzi.map((b) => (
                <tr key={b.ax}
                    className={grindaFocus === b.ax ? "vs-focus" : ""}>
                  <td><strong>{b.ax}</strong></td>
                  <td className="sg-muted">
                    {b.B_m}×{b.H_c_m} m · d = {n(b.d_m, 2)}
                    {b.h_talpa_m != null && (
                      t("struct.rzv.grinda_talpa", { v: n(b.h_talpa_m, 2) })
                    )}
                  </td>
                  <td>{n(b.M_poz_knm)}</td>
                  <td>{n(b.M_neg_knm)}</td>
                  <td>{n(b.V_kn)}</td>
                  <td>{n(b.As_jos_cm2, 2)} <span className="sg-muted">({b.guverneaza_jos})</span></td>
                  <td>{n(b.As_sus_cm2, 2)} <span className="sg-muted">({b.guverneaza_sus})</span></td>
                  <td className="sg-muted">{n(b.As_min_cm2, 2)}</td>
                  <td>{n(b.rho_pct, 3)} <span className="sg-muted">/ {n(b.rho_min_pct, 3)}</span></td>
                  <td>{n(b.V_Rd_c_kn)}</td>
                  <td>{b.etrieri_necesari
                    ? t("struct.rzv.etrieri_necesari_rho", { v: n(b.rho_w_min_pct, 3) })
                    : t("struct.rzv.nu")}</td>
                  <td><Da ok={b.ok_taietoare} /></td>
                  <td className="sg-muted">
                    {t("struct.rzv.consola_detaliu", {
                      lc: n(b.consola.L_consola_m, 2), m: n(b.consola.M_knm_m, 2),
                      as: n(b.consola.As_nec_cm2_m, 2),
                    })}
                    <br />
                    {t("struct.rzv.consola_panta", {
                      h: n(b.consola.h_talpa_m ?? b.h_talpa_m, 2),
                    })}{" "}
                    {b.consola.panta_ok ? "≥" : "<"} L_c{" "}
                    <Da ok={b.consola.panta_ok} />
                    {!b.consola.panta_ok && b.consola.panta_motiv && (
                      <> — {b.consola.panta_motiv}</>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {grinzi.length > 0 && (
            <p className="sg-muted" style={{ marginTop: 8 }}>
              {t("struct.rzv.armaturi_laterale", {
                sus: grinzi[0].armatura_sus,
                phi: grinzi[0].phi_etrier_min_mm,
                dubli: grinzi[0].etrieri_dubli ? t("struct.rzv.etrieri_dubli") : "",
                referinta: grinzi[0].referinta,
              })}
            </p>
          )}
        </>
      )}

      {/* -------------------------------------------------------------- MODEL */}
      {tab === "model" && (
        <>
          <Pas titlu={t("struct.rzv.modelata_fundatia_altfel")}
             rezumat={t("struct.rzv.grinzi_pat_resoarte")}>
            <p>{tx("struct.rzv.grinzile_stalpii_suprast")}</p>
            <p>{t("struct.rzv.trei_lucruri_fac")}</p>
            <ul>
              <li>{tx("struct.rzv.rigiditatea_li")}</li>
              <li>{tx("struct.rzv.sectiunea_li")}</li>
              <li>{tx("struct.rzv.b_pasul_resoartelor")}</li>
            </ul>
            <p>{tx("struct.rzv.arcurile_b_liniare")}</p>
          </Pas>
          <D.DiagramaWinkler />
          <div className="st-cards">
            <div className="st-card">
              <div className="st-card-label">{t("struct.rzv.resoarte_intinse2")}<EtichetaNatura cheie="desprindere" /><Marime cheie="desprindere" /></div>
              <div className="st-card-value">
                {n(g.resoarte?.maxim_pct, 1)} <span className="st-small">%</span>
              </div>
              <div className="st-card-sub">
                {t("struct.rzv.resoarte_sub", {
                  comb: g.resoarte?.guverneaza?.combinatie ?? "—",
                  n: g.resoarte?.nr_resoarte ?? "—",
                })}
              </div>
            </div>
            <div className="st-card">
              <div className="st-card-label">{t("struct.rzv.noduri_baza_axe2")}<Marime cheie="noduri_baza" /></div>
              <div className="st-card-value">{v.noduri_partajate ?? 0}</div>
              <div className="st-card-sub">
                {t("struct.rzv.noduri_sub", { n: v.noduri_orfane?.length ?? 0 })}
              </div>
            </div>
          </div>
          {(v.avertismente ?? []).length > 0 && (
            <div style={{ marginTop: 10 }}>
              {(v.avertismente ?? []).map((w, i) => (
                <div key={i} className="st-warn">⚠ {w}</div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "cautare" && (
        <>
          <ExplicaBucla />
          <TabCautare c={v.cautare} />
        </>
      )}

      {tab === "metoda" && (
        <AvertismenteMetoda avertismente={v.avertismente_metoda ?? []} />
      )}
    </div>
  );
}


// ------------------------------------------------------- căutarea geometriei
// Bucla LOCALĂ: ce a încercat motorul ca să treacă verificările, în ce ordine
// și de ce. Istoricul complet e partea importantă — proiectantul trebuie să
// poată reface raționamentul, nu doar să vadă rezultatul.

// Numele verificărilor picate: CHEI, nu texte. Lista vine de la server cu
// codul verificării, iar numele citibil e al interfeței — se traduce ca oricare
// altul, nu se îngheață în engleză sau în română la scrierea jobului.
const NUME_PICAT: Record<string, Cheie> = {
  presiune_ax: "struct.rzv.v_presiune_ax",
  presiune_stalp: "struct.rzv.v_presiune_stalp",
  desprindere: "struct.rzv.v_desprindere",
  tasare_relativa: "struct.rzv.v_tasare_relativa",
  lunecare: "struct.rzv.v_lunecarea",
  rasturnare: "struct.rzv.v_rasturnarea",
  subpresiune: "struct.rzv.v_subpresiunea",
  ridicare: "struct.rzv.v_ridicarea",
  strapungere: "struct.rzv.v_strapungerea",
  taietoare_grinda: "struct.rzv.v_taietoarea",
};

function TabCautare({ c }: { c?: CautareGeometrie }) {
  useT();
  if (!c || !c.activ) {
    return (
      <div className="sg-muted">
        {t("struct.rzv.bucla_locala_nu_rulat", { motiv: traduceRezultat(c?.motiv) })}
      </div>
    );
  }
  const g0 = c.geometrie_initiala!;
  const g1 = c.geometrie_finala!;
  const latiri = Object.entries(g1.B_pe_ax ?? {});

  return (
    <>
      <Pas titlu={t("struct.rzv.face_bucla_locala")}
             rezumat={t("struct.rzv.bucla_misca_doar")}>
        <p>{tx("struct.rzv.cand_verificare_nu_trece")}</p>
        <p>{tx("struct.rzv.motivul_refoloseste")}</p>
        <p>{tx("struct.rzv.alege_la_fiecare_pas")}</p>
        <p>
          {tx("struct.rzv.cand_opreste_totul_trece", {
            rezerva: c.rezerva_convergenta_pct
              ? t("struct.rzv.cu_cel_putin_rezerva", { n: c.rezerva_convergenta_pct })
              : t("struct.rzv.exact_pe_prag"),
            prag: bani(c.prag_eficienta_eur),
          })}
        </p>
        {!!c.rezerva_convergenta_pct && (
          <p>{tx("struct.rzv.de_ce_rezerva", { n: c.rezerva_convergenta_pct })}</p>
        )}
      </Pas>

      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.rzv.rezultat")} {c.convergent ? <span className="st-ok">✓</span>
                                   : <span className="st-bad">✗</span>}
            <Marime cheie="convergenta_cautare" />
          </div>
          <div className="st-card-value">
            {c.nr_picate_initial ?? 0} → {c.picate_ramase
              ? Object.values(c.picate_ramase).reduce((s, x) => s + x.nr, 0) : 0}
          </div>
          <div className="st-card-sub">
            {t("struct.rzv.picate_in_pasi", { n: c.iteratii, oprire: c.oprire })}
          </div>
        </div>
        <div className="st-card st-card-main">
          <div className="st-card-label">{t("struct.rzv.costul_fundatiei")}<Marime cheie="cost_structura" /></div>
          <div className="st-card-value">{bani(c.cost_final?.total_eur)}</div>
          <div className="st-card-sub">
            {c.cost_final?.beton_simplu
              ? t("struct.rzv.cost_beton_armat_simplu", {
                  armat: bani(c.cost_final.beton),
                  simplu: bani(c.cost_final.beton_simplu),
                })
              : ""}
            {t("struct.rzv.de_la_cost", { v: bani(c.cost_initial?.total_eur) })}
            {c.crestere_cost_pct != null && t("struct.rzv.crestere_pct", {
              v: `${c.crestere_cost_pct > 0 ? "+" : ""}${c.crestere_cost_pct}`,
            })}
            {t("struct.rzv.eur_pe_ml", {
              e: c.cost_final?.eur_pe_ml, l: c.cost_final?.lungime_m,
            })}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">{t("struct.rzv.geometria_gasita2")}<Marime cheie="optimizare_bd" /></div>
          <div className="st-card-value" style={{ fontSize: 22 }}>
            B {g1.B} · D<sub>f</sub> {g1.Df}
          </div>
          <div className="st-card-sub">
            {tx("struct.rzv.geometria_de_la", { b: g0.B, df: g0.Df })}
            {latiri.length > 0 && t("struct.rzv.axe_latite_local", { n: latiri.length })}
            <br />
            {tx("struct.rzv.h_derivat", { h: g1.Hc, h0: g0.Hc })}
            {g1.Ht ? t("struct.rzv.h_talpa_miscata", { v: g1.Ht }) : null}
          </div>
        </div>
      </div>

      {latiri.length > 0 && (
        <p className="sg-muted">
          <strong>{t("struct.rzv.latimi_diferite_axe")}</strong>{" "}
          {t("struct.rzv.latimi_lista", {
            lista: latiri.map(([k, b]) =>
              t("struct.rzv.ax_egal_m", { ax: k, b })).join(" · "),
            b: g1.B,
          })}
        </p>
      )}

      <div className="sg-muted" style={{ marginTop: 16, lineHeight: 1.5 }}>
        {t("struct.rzv.jurnalul_pas_pas")}
      </div>

      <Legenda
        titlu={t("struct.rzv.actiunile_catalog_cand")}
        coloane={[
          { nume: t("struct.rzv.f1_latirea_axelor"),
            ce: <>{t("struct.rzv.creste_b_doar")}</>,
            formula: t("struct.rzv.f1_formula"),
            validare: t("struct.rzv.f1_validare") },
          { nume: t("struct.rzv.f2_latirea_intregii"),
            ce: <>{t("struct.rzv.creste_b_peste")}</>,
            formula: "B += 10 cm",
            validare: t("struct.rzv.f2_validare") },
          { nume: t("struct.rzv.f3_coborarea_cotei"),
            ce: <>{t("struct.rzv.coboara_d_f")}</>,
            formula: t("struct.rzv.f3_formula"),
            validare: t("struct.rzv.f3_validare") },
          { nume: t("struct.rzv.f4_grinda_fundare"),
            ce: <>{t("struct.rzv.creste_h_c")}</>,
            formula: t("struct.rzv.f4_formula"),
            validare: t("struct.rzv.f4_validare") },
          { nume: t("struct.rzv.f5_portiuni_suplimentare"),
            ce: <>{t("struct.rzv.completeaza_golurile_axe")}</>,
            formula: t("struct.rzv.f5_formula"),
            validare: t("struct.rzv.f5_validare") },
          { nume: t("struct.rzv.f6_ingustarea_axelor"),
            ce: <>{t("struct.rzv.pas_economie_rulat")}</>,
            formula: t("struct.rzv.f6_formula"),
            validare: t("struct.rzv.f6_validare") },
          { nume: t("struct.rzv.f7_nume"),
            ce: <>{t("struct.rzv.celalalt_pas_economie")}</>,
            formula: t("struct.rzv.f7_formula"),
            validare: t("struct.rzv.f7_validare") },
        ]}
      />

      {c.picate_ramase && Object.keys(c.picate_ramase).length > 0 && (
        <div className="st-findings" style={{ marginTop: 12 }}>
          <h4>{t("struct.rzv.a_ramas_nerezolvat")}</h4>
          <ul>
            {Object.entries(c.picate_ramase).map(([tip, x]) => (
              <li key={tip}>
                <strong>{NUME_PICAT[tip] ? t(NUME_PICAT[tip]) : tip}</strong>
                {" "}— {tn("struct.rzv.n_cazuri#n", x.nr)}:{" "}
                <span className="sg-muted">{x.unde.join(", ")}</span>
              </li>
            ))}
          </ul>
          {c.diagnostic && <div className="st-warn">⚠ {c.diagnostic}</div>}
        </div>
      )}

      <p className="sg-muted" style={{ marginTop: 12 }}>{c.nota}</p>
    </>
  );
}
